import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/** The "How to use" walkthrough, mirroring the Commissions / Planning
 *  how-to-tab pattern. Grows as the feature does. */
/** Mirrors ``cash_flow.bank_accounts.match_require`` / ``match_exclude`` on the
 *  server, which is what actually does the matching. Written out here because
 *  the person naming the files cannot see that table, and a rename that
 *  silently stops matching costs a week of the close. */
const FILENAME_RULES = [
  { account: "1110 AFCU Checking", require: "afcu + checking", exclude: "",
    example: "AFCU Checking AccountHistory.csv" },
  { account: "1111 AFCU Savings", require: "afcu + savings",
    exclude: "mm, money, checking", example: "AFCU Savings AccountHistory.csv" },
  { account: "1112 AFCU Money Market", require: "afcu + (mm or money)", exclude: "",
    example: "AFCU Money Market Savings AccountHistory.csv" },
  { account: "1120 Zions Checking", require: "zions + checking", exclude: "",
    example: "Zions Checking Transactions-<date>-<id>.csv" },
  { account: "1122 Zions Treasury", require: "zions + (treasury or savings)",
    exclude: "checking", example: "Zions Fidelity Treasury Transactions-<date>-<id>.csv" },
  { account: "1130 BofA Checking", require: "bofa or boa, + checking", exclude: "",
    example: "BoA checking stmt.csv" },
  { account: "1131 BofA Savings", require: "bofa or boa, + savings", exclude: "",
    example: "BoA savings stmt.csv" },
];

export function HowToTab() {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>How to use</CardTitle>
          <CardDescription>
            The intended weekly flow. This section will grow as the feature does.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-2 pl-5 text-sm">
            <li>
              <span className="font-medium">Upload the week's seven exports by hand</span>, on
              the <span className="font-medium">Uploads</span> tab: set{" "}
              <span className="font-medium">Week ending</span> to the Sunday that just closed,
              select all seven files at once, and hit Ingest. Save them into{" "}
              <code className="rounded bg-muted px-1">
                06 Cash Forecasting/Bank Exports/&lt;YYYY-MM-DD&gt;/
              </code>{" "}
              as usual too, so the archive stays complete.
              <p className="mt-1 text-muted-foreground">
                The SharePoint pull is built but not connected yet — Microsoft Graph is not
                configured for this environment, and the Uploads tab will say so. Manual upload
                is the live path and runs exactly the same pipeline, so nothing about the
                numbers differs.
              </p>
            </li>
            <li>
              <span className="font-medium">Upload all seven together, and only for the week
              that just closed.</span> Both halves matter, for the same reason: an upload
              replaces an account's history with what the file contains, so a partial or
              backdated upload deletes the transactions it does not carry. In August 2026 a
              single Zions Treasury file uploaded against an older week erased three weeks of
              that account and broke a week that had already been locked. If an old week needs
              correcting, re-upload the <em>whole</em> set for the newest week — the exports
              carry full history, so that repairs the past as well.
            </li>
            <li>
              Each file is matched to its account by filename, parsed, and turned into
              transactions with a running balance, a week ending, and a cash bucket. Re-running
              a week <em>replaces</em> it rather than duplicating, so a corrected export of the
              current week is always safe to re-send — see the naming rules below.
            </li>
            <li>
              Check the <span className="font-medium">tie-out</span> on the ingest row: the sum
              of every account's latest bank balance, minus what the engine computes for that
              week. "Ties" means we reproduce the bank; a dollar figure means we don't, and
              that gets explained before the week is locked. It is the same number the{" "}
              <span className="font-medium">Lock week</span> button enforces, so a week that
              reads "Ties" here is a week the lock will accept.
            </li>
            <li>
              Clear the <span className="font-medium">Unclassified vendors</span> queue.
              Anything no rule recognised lands there, grouped by vendor. Adding a mapping rule
              reclassifies every past and future week in one pass — no re-import.
            </li>
            <li>
              The <span className="font-medium">Summary</span> tab updates — ending cash by
              week, monthly collections, and the runway extension — and the closed week appears
              in the week-by-week review.
            </li>
            <li>
              In the review, the finance team records why the week landed ahead of or behind
              forecast, and can have an LLM (local or Claude) draft the AI analysis from the
              run's numbers and the team's comments. If nobody triggers it, the AI writes its
              own analysis five days after the week ends.
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>What the files have to be called</CardTitle>
          <CardDescription>
            Files are matched to accounts by filename, so the bank's own export names are all
            that is needed — no renaming. Matching is case-insensitive and looks for words
            anywhere in the name, which is why "AFCU Checking export 9.csv" and "AFCU Checking
            AccountHistory.csv" both land in the same account. Keep this list current if a bank
            changes its naming.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Account</th>
                  <th className="py-2 pr-4 font-medium">Name must contain</th>
                  <th className="py-2 pr-4 font-medium">Must not contain</th>
                  <th className="py-2 font-medium">What the bank currently sends</th>
                </tr>
              </thead>
              <tbody>
                {FILENAME_RULES.map((r) => (
                  <tr key={r.account} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-4 whitespace-nowrap">{r.account}</td>
                    <td className="py-2 pr-4">
                      <code className="rounded bg-muted px-1 text-xs">{r.require}</code>
                    </td>
                    <td className="py-2 pr-4">
                      {r.exclude ? (
                        <code className="rounded bg-muted px-1 text-xs">{r.exclude}</code>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </td>
                    <td className="py-2 font-mono text-xs text-muted-foreground">{r.example}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Check the file list, not just the
            tie-out.</span> A name that matches nothing is listed as{" "}
            <span className="font-medium">unmatched</span> and that account simply has no
            transactions for the week — and because the missing account's balance carries
            forward from the week before, the tie-out can still read $0. The Uploads tab shows
            every file with its row count for exactly this reason: seven badges, seven accounts,
            every week.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Where the forecast numbers come from</CardTitle>
          <CardDescription>
            Every forecast week is the sum of its <span className="font-medium">drivers</span> —
            the rows on the Assumptions tab. Each driver gets its numbers one of four ways, and
            knowing which matters, because only two of them keep working on their own.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="flex flex-col gap-3 text-sm">
            <div>
              <dt className="font-medium">Seasonality — Customer Collections</dt>
              <dd className="text-muted-foreground">
                Trailing bank actuals become a monthly run rate, spread across the weeks of a
                month by the W1–W5 weights. Self-maintaining: it re-reads the bank every time.
                The only setting is the look-back window.
              </dd>
            </div>
            <div>
              <dt className="font-medium">New contracts</dt>
              <dd className="text-muted-foreground">
                A signed contract's monthly amount, recurring every 28 days from its first
                expected cash week. Self-maintaining once the contract is entered.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Calendars — payroll and recurring vendors</dt>
              <dd className="text-muted-foreground">
                Dated rows: each Rippling pay run and each Twilio / AMEX / RAMP payment, with
                the week the cash actually leaves. <span className="font-medium">These run
                out</span> and have to be extended.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Manual amounts</dt>
              <dd className="text-muted-foreground">
                Explicit per-week figures for rent, print, contractors and the rest.{" "}
                <span className="font-medium">These also run out.</span>
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Keeping the assumptions current</CardTitle>
          <CardDescription>
            This is the part that needs a human. Dated drivers do not repeat themselves — a
            driver with nothing scheduled contributes <span className="font-medium">zero</span>,
            not "the same as last month".
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 text-sm">
          <div className="rounded-md border border-warning/40 bg-warning-light p-3">
            <p className="font-medium">Why zero is the dangerous answer</p>
            <p className="mt-1 text-muted-foreground">
              A missing rent row doesn't look like an error — it looks like a month with no
              rent, and cash looks healthier than it is. That's deliberate: the alternative is
              inventing numbers nobody approved. So instead the app tells you where the forecast
              stops being fully driven.
            </p>
          </div>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <span className="font-medium">Weekly Detail</span> marks any week past that point{" "}
              <span className="font-medium text-warning">Partial</span> instead of Fcst, and
              shades the column. A banner above the grid names exactly which drivers to extend
              and how far each currently reaches. <span className="font-medium">Summary</span>{" "}
              carries the same banner, showing the warnings as they stood when that run was
              published — so an older run can name drivers that have since been extended, which
              is the point: it tells you what the run you are looking at actually knew.
            </li>
            <li>
              Payroll is stricter: past the loaded Rippling calendar the app forecasts{" "}
              <span className="font-medium">no payroll at all</span> and says so, rather than
              guessing a cadence. Load the next calendar before trusting a forecast that far
              out.
            </li>
            <li>
              Infrequent items are not mistaken for expired ones. A quarterly bill looks like a
              single row inside a 13-week window; the app infers each driver's own rhythm from
              its history, so it only warns when something has genuinely run out.
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>What you can change today, and where</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          <p className="text-muted-foreground">
            Everything below is edited in the app, and every change is written to the model
            change log with who made it and why.
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <span className="font-medium">Mapping tab</span> — add, edit, disable and reorder
              the rules that turn bank descriptions into buckets. Order is first-match-wins, so
              it's meaningful. "Re-apply to history" reclassifies every past week at once.
            </li>
            <li>
              <span className="font-medium">Uploads tab</span> — classify any vendor the rules
              didn't recognise, either as a one-off or as a new rule that fixes every past and
              future occurrence.
            </li>
            <li>
              <span className="font-medium">Assumptions tab</span> — the collections look-back
              window, plus a read-only view of the pay calendar, contracts, hires, vendors and
              weights.
            </li>
            <li>
              <span className="font-medium text-muted-foreground">
                Not yet editable in the app:
              </span>{" "}
              the pay calendar, new hires, contracts, recurring vendors and the manual weekly
              amounts. Changing those still needs a developer — ask, and say what should change
              and why, so it lands in the change log.
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reading the charts</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc space-y-2 pl-5 text-sm">
            <li>
              Solid lines are bank-tied actuals; dashed lines are the model forecast; dotted
              context past the model horizon is extrapolation, not model output. Out there each
              dated driver simply repeats its own rhythm, and anything with only one scheduled
              payment is left flat rather than being inflated into a recurring bill. The runway
              stops 22 weeks past the horizon because payroll has a hard end at the loaded pay
              calendar, and a runway drawn past it would quietly show a company that had
              stopped paying anyone.
            </li>
            <li>
              On the runway chart, the <span className="font-medium text-danger">solid red
              line</span> is zero cash and the{" "}
              <span className="font-medium text-danger">dashed red line</span> is the cash
              budget threshold, configured on the <span className="font-medium">Admin</span>{" "}
              tab. Weekly-detail rows below that budget show in red.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
